import { IOrganization } from 'types/models/OrganizationModels';
import { ITrackingDiagnostic } from 'types/models/TrackingDiagnosticsModel';
import { UserDiagnosticsState, UserDiagnosticsTag } from 'types/models/UserDiagnosticsModel';

export const filterHasLicense = (license: string[], organization: IOrganization) => {
    return organization.licenses && !!license.every((l) => organization.licenses.includes(l));
};

export const getLicenseFilterOptions = (licenses: string[]) => {
    return licenses.map(type => ({
        value: type,
        label: type
    }));
};

export const filterHasUserDiagnosticState = (value: string, state: UserDiagnosticsState) => {
    const enumValue = UserDiagnosticsState[value as keyof typeof UserDiagnosticsState];
    return enumValue === state;
};

export const filterHasUserDiagnosticTags = (tagStrings: string[], userDiagnosticsTags: UserDiagnosticsTag[] | null): boolean => {
    const userTags = userDiagnosticsTags ?? [];

    const requiredTags = tagStrings
        .map(tag => (UserDiagnosticsTag as any)[tag])
        .filter((tag): tag is UserDiagnosticsTag => typeof tag === "number");

    return requiredTags.every(tag => userTags.includes(tag));

};


// TypeScript numeric enums are bi-directional, meaning Object.entries will include both:
// - forward mappings: ["New", 0]
// - reverse mappings: [0, "New"]
// To avoid duplicate entries and ensure we only process the named enum members,
// we filter out entries where the value is not a number.
export function getEnumFilterOptions<T extends Record<string, string | number>>(enumObj: T) {
    return Object.entries(enumObj)
        .filter(([key, value]) => typeof value === "number")
        .map(([key]) => ({
            value: key,
            label: key
        }));
}

export const getFileState = (trackingDiagnostic: ITrackingDiagnostic): 'Uploaded' | 'Failed' | 'Pending' => {
    if (trackingDiagnostic.hasFile) return 'Uploaded';
    if (trackingDiagnostic.failureReason != null) return 'Failed';
    return 'Pending';
};

export const filterHasTrackingDiagnosticsHasFile = (value: string, trackingDiagnostic: ITrackingDiagnostic) => {
    return getFileState(trackingDiagnostic) === value;
};

export const filterHasTrackingDiagnosticsType = (types: string[], trackingDiagnostic: ITrackingDiagnostic) => {
    return Boolean(trackingDiagnostic.type) && types.includes(trackingDiagnostic.type);
};

export const filterHasTrackingDiagnosticsSessionType = (sessionTypes: string[], trackingDiagnostic: ITrackingDiagnostic) => {
    return Boolean(trackingDiagnostic.sessionType) && sessionTypes.includes(trackingDiagnostic.sessionType);
};

export const getStringArrayFilterOptions = (types: string[]) => {
    return types.map(type => ({
        value: type,
        label: type
    }));
};

export const getBooleanArrayFilterOptions = () => {
    return [
        { value: 'true', label: 'True' },
        { value: 'false', label: 'False' }
    ];
};
